/**
 * Based on dashboardPage.js in the dashboard folder
 * Renders a grid of SnapshotItem (a grid of live images)
 * Has the "paging" feature, which allows only 20 ravens to be displayed at a time 
 *      -- Renders the buttons for the pages: going to the first/last/next/previous page
 * Renders 
 * - grid size change buttons
 * - camera view toggle button from ViewToggle.js,
 * Rendered in GridPage.js
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';


import Analytics from 'APP_ROOT/services/analytics';
import Events from '../../common/events';
import NoRavensError from '../errors/noRavensError';


import SettingsSelectors from 'APP_ROOT/data/settings/selectors';
import { loadSettings } from 'APP_ROOT/data/settings/actions';
import Permissions from '../../../data/permissions';
import { Selectors as tagsSelectors } from '../../../data/tags';

import { RESOURCE_GROUP_TYPES } from '../dashboard/ResourceGroups/constants';

import SnapshotItem from './SnapshotItem';
import ViewToggle from './ViewToggle';

import './ImageGrid.scss';
import { strings } from '../../../localization';

// PaginationControls adapted from EventList.js
class PaginationControls extends React.PureComponent {
    static propTypes = {
        pageNumber: PropTypes.number.isRequired,
        maxPage: PropTypes.number.isRequired,
        goToPreviousPage: PropTypes.func.isRequired,
        goToNextPage: PropTypes.func.isRequired,
        goToFirstPage: PropTypes.func.isRequired,
        goToLastPage: PropTypes.func.isRequired,
    };

    render() {
        const { pageNumber, maxPage, goToPreviousPage, goToNextPage, goToFirstPage, goToLastPage } = this.props;
        
        if (maxPage <= 1) {
            return null; // Don't render pagination controls if there's only one page
        }

        return (
            <div className="control-pagination">
                <button onClick={goToFirstPage} disabled={pageNumber === 1}><i className="material-icons">first_page</i></button>
                <button onClick={goToPreviousPage} disabled={pageNumber === 1}><i className="material-icons">chevron_left</i></button>
                <div className="coaching-review-results-current-page">
                    {strings.page} {pageNumber} {strings.of} {maxPage}
                </div>
                <button onClick={goToNextPage} disabled={pageNumber === maxPage}><i className="material-icons">chevron_right</i></button>
                <button onClick={goToLastPage} disabled={pageNumber === maxPage}><i className="material-icons">last_page</i></button>
            </div>
        );
    }
}

const DEFAULT_UOM = 'IMPERIAL';
const PAGE_SIZE = 20;

class ImageGrid extends Component {

    constructor(props) {
        super(props);
        this.state = {
            enhancedVideoAccessModeActive: false,
            currentPage: 1,
            gridSize: 'full-grid', // 'full-grid', 'two-ravens', 'three-ravens'
        };
    }
    handleGridSizeChange = (size) => {
        this.setState({ gridSize: size });
    }

    onRefreshMediaEvent = e => {
        const selectedRavenUuid = this.props.selectedRaven && this.props.selectedRaven.ravenUuid;
        if (selectedRavenUuid) {
            document.dispatchEvent(new CustomEvent(Events.REFRESH_RAVEN, { detail: { ravenUuid: selectedRavenUuid } }));
        }
    }

    toggleEnhancedVideoAccessModeActive = () => {
        const isActive = !this.state.enhancedVideoAccessModeActive;
        this.setState({
            enhancedVideoAccessModeActive: isActive
        })
        if (!isActive) {
            const selectedRaven = this.props.selectedRaven ? this.props.selectedRaven : {};
            Events.enhancedVideoAccessRouteHighlightRequest(selectedRaven.ravenUuid, 0, 0); // <-- clear any current highlight
        }
    }

    updateMqttVisibility = () => {
        const { ravens, mqttService } = this.props;
        const { currentPage } = this.state;

        if (mqttService && ravens) {
            const startIndex = (currentPage - 1) * PAGE_SIZE;
            const paginatedRavens = ravens.slice(startIndex, startIndex + PAGE_SIZE);
            const visibleUuids = paginatedRavens.map(r => r.ravenUuid);

            mqttService.setVisibleRavens(visibleUuids);
        }
    }

    componentDidMount() {
        Analytics.pageView();
        this.props.enableDFast(true);
        this.updateMqttVisibility(); // Initial set
        document.addEventListener(Events.REFRESH_MEDIA, this.onRefreshMediaEvent);
    }

    goToFirstPage = () => {
        this.setState({ currentPage: 1 }, this.updateMqttVisibility);
    };

    goToPreviousPage = () => {
        this.setState(prev => ({
            currentPage: Math.max(1, prev.currentPage - 1)
        }), this.updateMqttVisibility);
    };

    goToNextPage = (totalPages) => {
        this.setState(prev => ({
            currentPage: Math.min(totalPages, prev.currentPage + 1)
        }), this.updateMqttVisibility);
    };

    goToLastPage = (totalPages) => {
        this.setState({ currentPage: totalPages }, this.updateMqttVisibility);
    };

    componentWillUnmount() {
        this.props.enableDFast(false);
        // Clear visibility when leaving so it doesn't interfere with Dashboard
        if (this.props.mqttService) {
            this.props.mqttService.setVisibleRavens([]);
        }
        document.removeEventListener(Events.REFRESH_MEDIA, this.onRefreshMediaEvent);
    }

    ravenStatus = (raven) => {

        if (raven.isIdling) {
            return (
                <div className="status-idling">
                    {strings.raven_status_idling}
                </div>
            )
        }

        if (raven.vehicleState.ravenOffline) {
            return (
                <div className="status-offline">
                    {strings.raven_status_offline}
                </div>
            )
        }

        if (raven.unplugged) {
            return (
                <div className="status-unplugged">
                    {strings.raven_status_unplugged}
                </div>
            );
        }

        if (raven.isDriving) {
            return (
                <div className="status-driving">
                    {strings.raven_status_driving}
                </div>
            );
        }

        if (!raven.isDriving) {
            return (
                <div className="status-parked">
                    {strings.raven_status_parked}
                </div>
            )
        }

    };


    render() {

        if (!this.props.ravens || this.props.ravens.length === 0) {
            return (
                <div id="dashboardPage" className="container page-container dashboard-page">
                    <NoRavensError {...this.props} />
                </div>
            );
        }

        const { ravens } = this.props;
        const { currentPage, gridSize } = this.state;

        const totalPages = Math.ceil(ravens.length / PAGE_SIZE);
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        const paginatedRavens = ravens.slice(startIndex, endIndex);
        const noButtons = totalPages === 1; //don't show pages buttons if there is only one page

        // Use gridSize from state
        let gridItemSize = gridSize;

        return (
            <div className="image-grid">
                <div className="snapshot-grid-header">
                    {/* Grid size control UI with handlers and selected state */}
                    <div className="grid-size-controls" title="Change grid size">
                        <button
                            className={`grid-size-btn${gridItemSize === 'two-ravens' ? ' selected' : ''}`}
                            type="button"
                            onClick={() => this.handleGridSizeChange('two-ravens')}
                            aria-label="Show larger snapshots"
                        >
                            <span className="material-icons">grid_view</span>
                        </button>
                        <button
                            className={`grid-size-btn${gridItemSize === 'three-ravens' ? ' selected' : ''}`}
                            type="button"
                            onClick={() => this.handleGridSizeChange('three-ravens')}
                            aria-label="Medium grid"
                        >
                            <span className="material-icons">view_module</span>
                        </button>
                        <button
                            className={`grid-size-btn${gridItemSize === 'full-grid' ? ' selected' : ''}`}
                            type="button"
                            onClick={() => this.handleGridSizeChange('full-grid')}
                            aria-label="Show more snapshots"
                        >
                            <span className="material-icons">view_comfy</span>
                        </button>
                    </div>

                    <div className="grid-camera-toggle-wrapper">
                        <ViewToggle
                            id="GRID_CAMERA_TOGGLE"
                            render={true}
                            onClick={this.props.toggleCamera}
                            camera={this.props.camera}
                        />
                    </div>

                    <PaginationControls
                        pageNumber={currentPage}
                        maxPage={totalPages}
                        goToFirstPage={this.goToFirstPage}
                        goToPreviousPage={this.goToPreviousPage}
                        goToNextPage={() => this.goToNextPage(totalPages)}
                        goToLastPage={() => this.goToLastPage(totalPages)}
                    />
                </div>

                <div className={`snapshot-grid ${gridItemSize}`}>
                    {paginatedRavens.map((raven) => (
                        <div
                            key={raven.ravenUuid}
                            className='snapshot-card-wrapper'
                        >
                            <button
                                className="snapshot-card"
                                onClick={() => this.props.goToSnapshot(raven.ravenUuid)}
                            >
                                <div className="snapshot-card-content">
                                    <div className='raven-info-top'>
                                        <div className="snapshot-raven-name">
                                            {raven.vehicle.name}
                                        </div>

                                        {this.ravenStatus(raven)}
                                    </div>

                                    <SnapshotItem
                                        className='live-grid-snapshot-image'
                                        {...this.props}
                                        stage={this.props.user.stage}
                                        theme={this.props.theme}
                                        features={this.props.features}
                                        enhancedVideoAccessModeActive={this.state.enhancedVideoAccessModeActive}
                                        toggleEnhancedVideoAccessModeActive={this.toggleEnhancedVideoAccessModeActive}

                                        selectedRaven={raven}
                                        selectedResource={{
                                            resourceType: RESOURCE_GROUP_TYPES.RAVEN,
                                            resourceId: raven.ravenUuid
                                        }}

                                        mqttService={this.props.mqttService}
                                        unitOfMeasure={
                                            this.props.unitOfMeasure
                                                ? this.props.unitOfMeasure.toUpperCase()
                                                : DEFAULT_UOM
                                        }

                                        currentSpeed={
                                            raven.vehicleState
                                                ? raven.vehicleState.currentSpeed
                                                : null
                                        }
                                        unplugged={raven.unplugged}
                                        sdCardStatus={raven.sdCardStatus}
                                        geofenceGeometryEditModeActive={this.props.geofenceGeometryEditModeActive}
                                        showcaseTargetRavenUuid={this.props.showcaseTargetRavenUuid}
                                        pokeMap={this.props.pokeMap}
                                    />

                                </div>
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.props.onDismissRaven(raven.ravenUuid);
                                }}
                                className="dismiss-btn"
                                title="Dismiss Raven"
                            >
                                <i className='material-icons close-snapshot-card'>cancel</i>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        hasAccessToAssets: Permissions.selectors.can(state, 'assets', 'read'),
        tags: tagsSelectors.tags(state),
        tagsLoaded: tagsSelectors.loaded(state),
        tagsError: tagsSelectors.error(state),
        tagsPending: tagsSelectors.pending(state),
        tagsLoading: tagsSelectors.loading(state),
        settingsLoaded: SettingsSelectors.settingsLoaded(state),
        unitOfMeasure: SettingsSelectors.unitOfMeasure(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        loadSettings: () => dispatch(loadSettings(ownProps.user && ownProps.user.stage)),
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(ImageGrid);

